/**
 * Right-side agent drawer.
 *
 * SDK-managed chat (`useAgentChat`) behind the `ai-chat` flag. Closing the
 * drawer aborts any in-flight stream.
 */
import { Box, IconButton, Toolbar, Typography } from '@mui/material';
import { X } from 'lucide-react';
import { useCallback, useEffect, useMemo } from 'react';
import { useRecoilState } from 'recoil';
import Sidebar from '~/components/Sidebar';
import { chatDrawerOpenState } from '~/state/atoms/chatAtoms';
import {
    useAgentChat,
    createBrowserRecorderFactory,
    transcribeViaRoute,
    useAgentRequestHeaders,
} from '~/agent';
import ChatInput, { type ChatInputDictationDeps } from './ChatInput';
import ChatMessageList from './ChatMessageList';

const DRAWER_WIDTH = 360;

export default function ChatDrawer() {
    const [open, setOpen] = useRecoilState(chatDrawerOpenState);
    const {
        messages,
        status,
        errorText,
        sendText,
        stop,
        proposalsByTool,
        applyProposal,
        rejectProposal,
        showProposalPreview,
        hideProposalPreview,
    } = useAgentChat();
    const busy = status === 'submitted' || status === 'streaming';

    const handleClose = useCallback(() => {
        // Closing the drawer aborts the in-flight stream — never a stuck wait.
        stop();
        setOpen(false);
    }, [stop, setOpen]);

    // Clear any hover-preview overlay still showing when the drawer closes
    // (e.g. closed by keyboard while the cursor was over a card, so no
    // mouse-leave fired). Watches `open` so it covers every close path.
    useEffect(() => {
        if (open) {
            return;
        }
        for (const [toolName, views] of Object.entries(proposalsByTool)) {
            for (const toolCallId of Object.keys(views)) {
                hideProposalPreview(toolName, toolCallId);
            }
        }
    }, [open, proposalsByTool, hideProposalPreview]);

    // Dictation deps.
    const agentRequestHeaders = useAgentRequestHeaders();
    const dictation = useMemo<ChatInputDictationDeps>(
        () => ({
            recorderFactory: createBrowserRecorderFactory(),
            transcribe: async (audio, mimeType, signal) =>
                transcribeViaRoute(
                    audio,
                    mimeType,
                    signal,
                    await agentRequestHeaders()
                ),
        }),
        [agentRequestHeaders]
    );

    return (
        <Sidebar open={open} side="right" width={DRAWER_WIDTH}>
            <Box
                display="flex"
                flexDirection="column"
                height="100%"
                width={DRAWER_WIDTH}
                sx={{ backgroundColor: 'background.paper' }}
            >
                <Toolbar variant="dense" />
                <Box
                    display="flex"
                    flexDirection="column"
                    flex={1}
                    overflow="hidden"
                    sx={(theme) => ({
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        mb: 1,
                        mr: '8px',
                    })}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        px={1.5}
                        py={1.5}
                        borderBottom={1}
                        borderColor="divider"
                    >
                        <Typography
                            variant="subtitle1"
                            fontSize={16}
                            fontWeight={500}
                        >
                            MVision Agent
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={handleClose}
                            title="Close"
                        >
                            <X size={16} />
                        </IconButton>
                    </Box>

                    <ChatMessageList
                        messages={messages}
                        busy={busy}
                        proposalsByTool={proposalsByTool}
                        onApply={applyProposal}
                        onReject={rejectProposal}
                        onPreviewEnter={showProposalPreview}
                        onPreviewLeave={hideProposalPreview}
                    />
                    {errorText && !busy && (
                        <Typography
                            role="alert"
                            variant="caption"
                            color="error"
                            sx={{ px: 1.5, pb: 0.5 }}
                        >
                            {errorText}
                        </Typography>
                    )}
                    {/* Drop dictation when closed: the Sidebar keeps this
                        shell mounted, so unmounting DictationControls is what
                        releases the mic on close. */}
                    <ChatInput
                        onSend={sendText}
                        disabled={busy}
                        dictation={open ? dictation : undefined}
                    />
                </Box>
            </Box>
        </Sidebar>
    );
}
